import React, { useState } from 'react';
import {
  CheckCircle, XCircle, Rocket, ChevronDown, ChevronUp, Calendar, Loader2, Edit,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

export const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    approved: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    live: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  };
  const c = config[status] || config.pending;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${c.bg} ${c.text} border ${c.border}`}>
      {status}
    </span>
  );
};

const EDIT_CATEGORIES = [
  { id: 'markets', name: 'Markets', subcategories: ['us_markets', 'global_markets', 'commodities', 'forex'] },
  { id: 'crypto', name: 'Crypto', subcategories: ['bitcoin', 'ethereum', 'altcoins', 'defi', 'nfts'] },
  { id: 'tech', name: 'Tech', subcategories: ['ai_ml', 'big_tech', 'gaming', 'social_media'] },
  { id: 'finance', name: 'Finance', subcategories: ['federal_reserve', 'banking', 'interest_rates', 'inflation'] },
  { id: 'sports', name: 'Sports', subcategories: ['football_soccer', 'nfl', 'nba', 'mlb', 'nhl', 'tennis', 'golf', 'mma_ufc', 'boxing', 'cricket', 'rugby', 'formula_1', 'olympics', 'esports'] },
  { id: 'science', name: 'Science', subcategories: ['space', 'climate', 'medicine', 'physics'] },
  { id: 'economics', name: 'Economics', subcategories: ['gdp', 'employment', 'trade', 'recession'] },
  { id: 'world_events', name: 'World Events', subcategories: ['elections', 'geopolitics', 'conflicts', 'diplomacy'] },
  { id: 'culture', name: 'Culture', subcategories: ['movies', 'tv_shows', 'music', 'awards'] },
  { id: 'pop_culture', name: 'Pop Culture', subcategories: ['celebrities', 'viral_trends', 'social_media', 'memes'] },
  { id: 'black_culture', name: 'Black Culture', subcategories: ['entertainment', 'sports', 'business', 'politics'] },
  { id: 'urban_culture', name: 'Urban Culture', subcategories: ['hip_hop', 'fashion', 'streetwear', 'sneakers'] },
  { id: 'continents', name: 'Continents', subcategories: ['north_america', 'south_america', 'europe', 'asia', 'africa', 'oceania', 'middle_east'] },
];

const IdeaRow = ({
  idea, onApprove, onReject, onLoadToMarket, onEditIdea, onResolveMarket,
  expanded, onToggle, selected, onSelect, showCheckbox,
}) => {
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [closesAt, setClosesAt] = useState('');
  const [initialPrice, setInitialPrice] = useState(50);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveData, setResolveData] = useState({
    winning_outcome: 'YES',
    resolution_reason: '',
  });
  const [editData, setEditData] = useState({
    title: idea.title,
    description: idea.description,
    category: idea.category || '',
    subcategory: idea.subcategory || '',
  });
  const [saving, setSaving] = useState(false);

  const handleLoadToMarket = async () => {
    if (!closesAt) return;
    setLoading(true);
    try {
      await onLoadToMarket(idea.id, closesAt, initialPrice);
      setShowLoadModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await onEditIdea(idea.id, editData);
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResolveMarket = async () => {
    if (!resolveData.winning_outcome) return;
    setResolving(true);
    try {
      await onResolveMarket(idea.market_id, resolveData.winning_outcome, resolveData.resolution_reason);
      setShowResolveModal(false);
      setResolveData({ winning_outcome: 'YES', resolution_reason: '' });
    } catch (error) {
      console.error('Failed to resolve market:', error);
    } finally {
      setResolving(false);
    }
  };

  const isValidForApproval = idea.has_category !== false && idea.has_subcategory !== false;
  const currentEditCategory = EDIT_CATEGORIES.find((c) => c.id === editData.category);
  const availableSubcategories = currentEditCategory?.subcategories || [];

  return (
    <>
      <div className={`bg-[#131b2e] rounded-xl border ${selected ? 'border-[#3B82F6]' : 'border-[#1e2a45]'} overflow-hidden`}>
        <div
          className="p-4 cursor-pointer hover:bg-[#1a2438] transition-colors"
          onClick={onToggle}
        >
          <div className="flex items-start justify-between gap-4">
            {showCheckbox && (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => onSelect(idea.id, e.target.checked)}
                  disabled={!isValidForApproval}
                  className="w-4 h-4 rounded border-[#1e2a45] bg-[#1e2a45] text-[#3B82F6] focus:ring-[#3B82F6] disabled:opacity-50"
                  data-testid={`select-idea-${idea.id}`}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge status={idea.status} />
                <span className="text-xs text-gray-500">{idea.category || 'No category'}</span>
                {idea.subcategory && (
                  <>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500">{idea.subcategory}</span>
                  </>
                )}
                <span className="text-xs text-gray-500">•</span>
                <span className="text-xs text-gray-500">{idea.votes} votes</span>
                {!isValidForApproval && (
                  <span className="text-xs text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">Missing {!idea.has_category && 'category'}{!idea.has_category && !idea.has_subcategory && '/'}{!idea.has_subcategory && 'subcategory'}</span>
                )}
              </div>
              <h3 className="text-white font-medium truncate">{idea.title}</h3>
              <p className="text-gray-500 text-sm mt-1">by {idea.author_name}</p>
            </div>
            <div className="flex items-center gap-2">
              {expanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </div>

        {expanded && (
          <div className="px-4 pb-4 border-t border-[#1e2a45] pt-4">
            <p className="text-gray-400 text-sm mb-4">{idea.description}</p>

            <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-4">
              <span>Outcome: {idea.outcome_type === 'triple' ? 'Win/Lose/Draw' : 'Yes/No'}</span>
              {idea.expires_at && (
                <span>Expires: {new Date(idea.expires_at).toLocaleDateString()}</span>
              )}
              <span>Created: {new Date(idea.created_at).toLocaleDateString()}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {idea.status === 'pending' && (
                <>
                  <Button
                    onClick={(e) => { e.stopPropagation(); setShowEditModal(true); }}
                    size="sm"
                    data-testid={`edit-idea-${idea.id}`}
                    className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={(e) => { e.stopPropagation(); onApprove(idea.id); }}
                    size="sm"
                    data-testid={`approve-idea-${idea.id}`}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Approve
                  </Button>
                  <Button
                    onClick={(e) => { e.stopPropagation(); onReject(idea.id); }}
                    size="sm"
                    data-testid={`reject-idea-${idea.id}`}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Reject
                  </Button>
                </>
              )}
              {idea.status === 'approved' && (
                <Button
                  onClick={(e) => { e.stopPropagation(); setShowLoadModal(true); }}
                  size="sm"
                  data-testid={`load-market-${idea.id}`}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs"
                >
                  <Rocket className="h-3 w-3 mr-1" />
                  Load to Market
                </Button>
              )}
              {idea.status === 'live' && idea.market_id && (
                <span className="text-xs text-green-400">Market ID: {idea.market_id}</span>
              )}
              {idea.status === 'live' && idea.market_id && (
                <Button
                  size="sm"
                  onClick={() => setShowResolveModal(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                  data-testid={`resolve-market-${idea.id}`}
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Resolve
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Idea Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="h-5 w-5 text-[#8B5CF6]" />
              Edit Idea
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Edit the idea details including category and subcategory.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData((prev) => ({ ...prev, title: e.target.value }))}
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg resize-none focus:outline-none focus:border-[#3B82F6]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <select
                value={editData.category}
                onChange={(e) => setEditData((prev) => ({ ...prev, category: e.target.value, subcategory: '' }))}
                className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg focus:outline-none focus:border-[#3B82F6]"
              >
                <option value="">Select category...</option>
                {EDIT_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Subcategory *</label>
              <select
                value={editData.subcategory}
                onChange={(e) => setEditData((prev) => ({ ...prev, subcategory: e.target.value }))}
                disabled={!editData.category}
                className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg focus:outline-none focus:border-[#3B82F6] disabled:opacity-50"
              >
                <option value="">Select subcategory...</option>
                {availableSubcategories.map((sub) => (
                  <option key={sub} value={sub}>{sub.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowEditModal(false)}
                variant="outline"
                className="flex-1 border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving || !editData.title || !editData.category || !editData.subcategory}
                className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load to Market Modal */}
      <Dialog open={showLoadModal} onOpenChange={setShowLoadModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Rocket className="h-5 w-5 text-[#3B82F6]" />
              Load Idea to Market
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Convert this approved idea into a live prediction market.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-[#1e2a45]/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">{idea.title}</h4>
              <p className="text-gray-400 text-xs">{idea.description}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Market Closes At *
              </label>
              <Input
                type="datetime-local"
                value={closesAt}
                onChange={(e) => setClosesAt(e.target.value)}
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Initial YES Price: {initialPrice}%
              </label>
              <input
                type="range"
                min="1"
                max="99"
                value={initialPrice}
                onChange={(e) => setInitialPrice(parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1% (NO favored)</span>
                <span>50% (Even)</span>
                <span>99% (YES favored)</span>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowLoadModal(false)}
                className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLoadToMarket}
                disabled={loading || !closesAt}
                className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
                Create Market
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve Market Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-amber-500" />
              Resolve Market
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Resolve this market and settle all positions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-[#1e2a45]/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">{idea.title}</h4>
              <p className="text-gray-400 text-xs">Market ID: {idea.market_id}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Winning Outcome *
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setResolveData((prev) => ({ ...prev, winning_outcome: 'YES' }))}
                  className={`flex-1 ${resolveData.winning_outcome === 'YES'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-[#1e2a45] hover:bg-[#2d3a56] text-gray-300'}`}
                >
                  YES
                </Button>
                <Button
                  type="button"
                  onClick={() => setResolveData((prev) => ({ ...prev, winning_outcome: 'NO' }))}
                  className={`flex-1 ${resolveData.winning_outcome === 'NO'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-[#1e2a45] hover:bg-[#2d3a56] text-gray-300'}`}
                >
                  NO
                </Button>
                <Button
                  type="button"
                  onClick={() => setResolveData((prev) => ({ ...prev, winning_outcome: 'TIE' }))}
                  data-testid="resolve-tie-btn"
                  className={`flex-1 ${resolveData.winning_outcome === 'TIE'
                    ? 'bg-zinc-500 hover:bg-zinc-600 text-white'
                    : 'bg-[#1e2a45] hover:bg-[#2d3a56] text-gray-300'}`}
                >
                  TIE
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                <span className="text-amber-400">TIE</span> refunds 50¢/share to every holder on both sides. <span className="text-green-400">YES</span> / <span className="text-red-400">NO</span> pays 1.00 PLAY/share to the winning side only.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Resolution Reason *
              </label>
              <Textarea
                value={resolveData.resolution_reason}
                onChange={(e) => setResolveData((prev) => ({ ...prev, resolution_reason: e.target.value }))}
                placeholder="Explain why this market is being resolved with this outcome..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white min-h-[100px]"
              />
              <p className="text-xs text-gray-500 mt-1">This reason will be recorded in the audit trail.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowResolveModal(false)}
                className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResolveMarket}
                disabled={resolving || !resolveData.resolution_reason.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {resolving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Resolve Market
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IdeaRow;
